import { Check, Pencil } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { formatBusinessDate } from "@/utils/datetime";
import { useColumnResize } from "@/hooks/useColumnResize";
import { useSpreadsheetGrid } from "@/hooks/useSpreadsheetGrid";
import { isDueSoon, isOverdue, money } from "./payrollUtils";

const featuredDate = (profile) => {
  const payment = profile.featured_payment;
  if (!payment) return "—";
  if (profile.type === "in_house") return formatBusinessDate(payment.payment_date);
  return formatBusinessDate(payment.paid_date || payment.due_date || payment.submitted_date);
};

const dateLabel = (profile) => {
  const payment = profile.featured_payment;
  if (!payment) return "Payment date";
  if (profile.type === "in_house") return "Paid";
  if (payment.status === "paid") return "Paid";
  return "Due";
};

const StatusPill = ({ profile }) => {
  const payment = profile.featured_payment;
  if (!payment) return <span data-testid={`payroll-row-status-${profile.id}`}>No payments</span>;
  const overdue = isOverdue(payment);
  const dueSoon = isDueSoon(payment);
  let label = payment.status === "paid" ? "Paid" : "Unpaid";
  let color = payment.status === "paid" ? "#166534" : "#334155";
  if (overdue) { label = "Overdue"; color = "#dc2626"; }
  else if (dueSoon) { label = "Due soon"; color = "#b45309"; }
  return (
    <strong data-testid={`payroll-row-status-${profile.id}`} style={{ color }}>
      {label}
    </strong>
  );
};

// Contractor payroll grid uses one column per user-requested field:
//   Contractor name · Date submitted · Paid date · Amount · Notes · [actions]
// In-house table keeps its original layout.
const CONTRACTOR_COLUMNS = [
  { key: "name", label: "Contractor name", align: "left" },
  { key: "submitted_date", label: "Date submitted", align: "left" },
  { key: "paid_date", label: "Paid date", align: "left" },
  { key: "amount", label: "Amount", align: "right" },
  { key: "notes", label: "Notes", align: "left" },
];

const IN_HOUSE_COLUMNS = [
  { key: "name", label: "Name", align: "left" },
  { key: "payment_date", label: "Payment date", align: "left" },
  { key: "status", label: "Status", align: "left" },
  { key: "amount", label: "Amount", align: "right" },
  { key: "notes", label: "Notes", align: "left" },
];

const ProfileTableRow = ({ profile, tab, inactive, onEdit, onMarkPaid, onRecord, cellProps }) => {
  const payment = profile.featured_payment;
  const canMarkPaid = Boolean(payment && payment.status === "unpaid");
  const columns = tab === "contractor" ? CONTRACTOR_COLUMNS : IN_HOUSE_COLUMNS;
  const cellFor = (colKey) => (cellProps ? cellProps({ rowKey: profile.id, colKey }) : {});

  const renderCell = (col) => {
    switch (col.key) {
      case "name":
        return (
          <td
            {...cellFor("name")}
            data-testid={`payroll-row-name-${profile.id}`}
            key="name"
          >
            <strong>{profile.name}</strong>
            {inactive && <span style={{ marginLeft: "0.4rem", color: "#64748b" }}>· Inactive</span>}
          </td>
        );
      case "submitted_date":
        return (
          <td {...cellFor("submitted_date")} data-testid={`payroll-row-submitted-${profile.id}`} key="submitted_date">
            {formatBusinessDate(payment?.submitted_date)}
          </td>
        );
      case "paid_date":
        return (
          <td
            {...cellFor("paid_date")}
            data-testid={`payroll-row-paid-${profile.id}`}
            key="paid_date"
            style={payment?.status !== "paid" ? { color: "#94a3b8" } : undefined}
          >
            {formatBusinessDate(payment?.paid_date)}
          </td>
        );
      case "payment_date":
        return (
          <td {...cellFor("payment_date")} data-testid={`payroll-row-date-${profile.id}`} key="payment_date">
            <span style={{ color: "#64748b", fontSize: "0.78rem" }}>{dateLabel(profile)}</span>
            <br />
            {featuredDate(profile)}
          </td>
        );
      case "status":
        return (
          <td {...cellFor("status")} key="status">
            <StatusPill profile={profile} />
          </td>
        );
      case "amount":
        return (
          <td
            {...cellFor("amount")}
            data-testid={`payroll-row-amount-${profile.id}`}
            key="amount"
            style={{ textAlign: "right", fontVariantNumeric: "tabular-nums", ...(cellFor("amount").style || {}) }}
          >
            {payment ? money(payment.amount) : "—"}
          </td>
        );
      case "notes":
        return (
          <td
            {...cellFor("notes")}
            data-testid={`payroll-row-notes-${profile.id}`}
            key="notes"
            style={{
              color: "#475569",
              maxWidth: "18rem",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              ...(cellFor("notes").style || {}),
            }}
            title={payment?.notes || ""}
          >
            {payment?.notes || ""}
          </td>
        );
      default:
        return null;
    }
  };

  return (
    <tr
      data-testid={`payroll-row-${profile.id}`}
      style={inactive ? { opacity: 0.7 } : undefined}
    >
      {columns.map(renderCell)}
      <td>
        <div style={{ display: "flex", gap: "0.4rem", justifyContent: "flex-end", whiteSpace: "nowrap" }}>
          {canMarkPaid && (
            <button
              className="table-edit-button"
              data-testid={`payroll-row-mark-paid-${profile.id}`}
              onClick={() => onMarkPaid(profile)}
              style={{ borderColor: "#166534", color: "#166534" }}
              title="Mark this payment paid (today)"
              type="button"
            >
              <Check size={13} /> Mark Paid
            </button>
          )}
          <button
            className="table-edit-button"
            data-testid={`payroll-row-quick-edit-${profile.id}`}
            disabled={!payment}
            onClick={() => onEdit(profile)}
            title={payment ? "Quick edit this payment" : "No payment to edit yet — open Record to add one"}
            type="button"
          >
            <Pencil size={13} /> Edit
          </button>
          <button
            className="table-open-link"
            data-testid={`payroll-row-record-${profile.id}`}
            onClick={() => onRecord(profile.id)}
            type="button"
          >
            Record
          </button>
        </div>
      </td>
    </tr>
  );
};

const ProfileTable = ({ profiles, tab, inactive, onEdit, onMarkPaid, onRecord, testId, emptyMessage, userId }) => {
  const columns = tab === "contractor" ? CONTRACTOR_COLUMNS : IN_HOUSE_COLUMNS;
  const columnResize = useColumnResize({ boardKey: `payroll-${tab}`, userId });
  const grid = useSpreadsheetGrid({
    onEnter: (rowKey) => {
      const profile = profiles.find((p) => p.id === rowKey);
      if (profile) onEdit(profile);
    },
  });
  return (
    <div className="board-table-scroll" data-testid={testId}>
      <table className="operations-data-table spreadsheet-grid" {...grid.tableProps}>
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col.key} scope="col" style={{ textAlign: col.align, ...(columnResize.headerStyle(col.key) || {}) }}>
                <span data-mobile-label={col.label}>{col.label}</span>
                <button
                  aria-label={`Resize ${col.label} column`}
                  className="col-resize-handle"
                  data-testid={`payroll-col-resize-${col.key}`}
                  onDoubleClick={() => columnResize.resetColumn(col.key)}
                  onPointerDown={(event) => columnResize.beginDrag(event, col.key)}
                  tabIndex={-1}
                  title="Drag to resize · double-click to reset"
                  type="button"
                />
              </th>
            ))}
            <th style={{ textAlign: "right" }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {profiles.map((profile) => (
            <ProfileTableRow
              cellProps={grid.cellProps}
              inactive={inactive}
              key={profile.id}
              onEdit={onEdit}
              onMarkPaid={onMarkPaid}
              onRecord={onRecord}
              profile={profile}
              tab={tab}
            />
          ))}
          {profiles.length === 0 && (
            <tr>
              <td colSpan={columns.length + 1} data-testid={`${testId}-empty`} style={{ color: "#64748b" }}>
                {emptyMessage}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export const PayrollProfileList = ({
  tab,
  summary,
  showAddForm,
  setShowAddForm,
  newProfile,
  setNewProfile,
  createProfile,
  search,
  setSearch,
  sortBy,
  setSortBy,
  activeProfiles,
  inactiveProfiles,
  inactiveOpen,
  setInactiveOpen,
  onExportCompany,
  onSelect,
  onMarkPaid,
  onQuickEdit,
}) => {
  const { user } = useAuth();
  const noun = tab === "in_house" ? "people" : "contractors";
  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.75rem" }}>
        <h2 style={{ margin: 0 }}>{tab === "in_house" ? "In-House people" : "Contractors"}</h2>
        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
          <button data-testid="payroll-export-company" onClick={onExportCompany} type="button">
            Export company CSV
          </button>
          <button
            className="invoice-primary-button"
            data-testid="payroll-add-toggle"
            onClick={() => setShowAddForm((open) => !open)}
            type="button"
          >
            {showAddForm ? "Close" : `+ Add ${tab === "in_house" ? "person" : "contractor"}`}
          </button>
        </div>
      </div>

      {summary && (
        <div className="workspace-grid" data-testid="payroll-grand-totals" style={{ margin: "0.5rem 0" }}>
          <span data-testid="payroll-grand-paid">Company paid: {money(summary.paid_total)}</span>
          <strong data-testid="payroll-grand-outstanding">
            Company outstanding: {money(summary.outstanding_total)}
          </strong>
          {summary.overdue_count > 0 && (
            <strong data-testid="payroll-grand-overdue" style={{ color: "#dc2626" }}>
              Company overdue: {summary.overdue_count} ({money(summary.overdue_total)})
            </strong>
          )}
        </div>
      )}

      {showAddForm && (
        <form className="field-note-form" data-testid="payroll-profile-form" onSubmit={createProfile}>
          <label htmlFor="payroll-profile-name">
            Name
            <input
              data-testid="payroll-profile-name"
              id="payroll-profile-name"
              onChange={(event) => setNewProfile({ ...newProfile, name: event.target.value })}
              required
              value={newProfile.name}
            />
          </label>
          <label htmlFor="payroll-profile-address">
            Address
            <input
              data-testid="payroll-profile-address"
              id="payroll-profile-address"
              onChange={(event) => setNewProfile({ ...newProfile, address: event.target.value })}
              value={newProfile.address}
            />
          </label>
          <label htmlFor="payroll-profile-tax">
            SSN / Tax info
            <input
              data-testid="payroll-profile-tax"
              id="payroll-profile-tax"
              onChange={(event) => setNewProfile({ ...newProfile, tax_info: event.target.value })}
              value={newProfile.tax_info}
            />
          </label>
          {tab === "in_house" ? (
            <label htmlFor="payroll-profile-rate">
              Default rate (optional)
              <input
                data-testid="payroll-profile-rate"
                id="payroll-profile-rate"
                min="0"
                onChange={(event) => setNewProfile({ ...newProfile, default_rate: Number(event.target.value) })}
                step="0.01"
                type="number"
                value={newProfile.default_rate}
              />
            </label>
          ) : (
            <label htmlFor="payroll-profile-term">
              Payment term
              <select
                data-testid="payroll-profile-term"
                id="payroll-profile-term"
                onChange={(event) => setNewProfile({ ...newProfile, payment_term: event.target.value })}
                value={newProfile.payment_term}
              >
                <option value="2_weeks">2 weeks</option>
                <option value="3_weeks">3 weeks</option>
              </select>
            </label>
          )}
          <label className="wide-field" htmlFor="payroll-profile-notes">
            Notes
            <textarea
              data-testid="payroll-profile-notes"
              id="payroll-profile-notes"
              onChange={(event) => setNewProfile({ ...newProfile, notes: event.target.value })}
              value={newProfile.notes}
            />
          </label>
          <button className="invoice-primary-button" data-testid="payroll-create-profile" type="submit">
            Add {tab === "in_house" ? "person" : "contractor"}
          </button>
        </form>
      )}

      <div style={{ display: "flex", gap: "0.5rem", margin: "0.5rem 0", alignItems: "center" }}>
        <label htmlFor="payroll-search" style={{ flex: 1 }}>
          <input
            data-testid="payroll-search"
            id="payroll-search"
            onChange={(event) => setSearch(event.target.value)}
            placeholder={`Search ${noun} by name`}
            style={{ width: "100%" }}
            type="search"
            value={search}
          />
        </label>
        <select
          aria-label="Sort profiles"
          data-testid="payroll-sort"
          onChange={(event) => setSortBy(event.target.value)}
          value={sortBy}
        >
          <option value="name">Sort: Name (A–Z)</option>
          <option value="recent">Sort: Most recently paid</option>
          <option value="outstanding">Sort: Outstanding balance</option>
        </select>
      </div>

      <ProfileTable
        emptyMessage={
          search.trim() ? `No active ${noun} match "${search.trim()}".` : `No active ${noun} yet.`
        }
        onEdit={onQuickEdit}
        onMarkPaid={onMarkPaid}
        onRecord={onSelect}
        profiles={activeProfiles}
        tab={tab}
        testId="payroll-profile-list"
        userId={user?.id}
      />

      {inactiveProfiles.length > 0 && (
        <section data-testid="payroll-inactive-archive" style={{ marginTop: "0.75rem" }}>
          <button
            data-testid="payroll-inactive-toggle"
            onClick={() => setInactiveOpen((open) => !open)}
            style={{ display: "flex", justifyContent: "space-between", width: "100%", gap: "0.75rem" }}
            type="button"
          >
            <span>{inactiveOpen ? "▾" : "▸"} Inactive archive</span>
            <strong data-testid="payroll-inactive-count">{inactiveProfiles.length}</strong>
          </button>
          {inactiveOpen && (
            <ProfileTable
              emptyMessage="No inactive profiles."
              inactive
              onEdit={onQuickEdit}
              onMarkPaid={onMarkPaid}
              onRecord={onSelect}
              profiles={inactiveProfiles}
              tab={tab}
              testId="payroll-inactive-list"
              userId={user?.id}
            />
          )}
        </section>
      )}
    </>
  );
};
